// Shared chrome data: the nav spine from the mockups, plus the contact
// details the header and footer both render.
//
// There is no phone number here, and that is a product decision rather than
// a missing value (open-items review, item 9). Ask-HVAC.com is an
// informational and decision-support site, not a dispatch desk — a visitor
// who wants professional help should land in the Second Opinion form, where
// their answers travel with them. So every "talk to someone" affordance
// points at SECOND_OPINION_HREF and there is no tap-to-call anywhere.
export const SITE_LOCATION = 'St. Augustine, FL'
export const SITE_REGION = 'St. Augustine & Northeast Florida'

/**
 * The site's administrative mailbox (open-items review, item 10).
 *
 * Monitored for site problems, corrections, privacy requests and technical
 * issues. Deliberately NOT an HVAC service or sales inbox — anyone wanting
 * work done goes through the form, and the legal pages say so where they
 * print this address.
 */
export const SITE_EMAIL = 'info@ask-hvac.com'

/**
 * How the site refers to the professional a lead reaches.
 *
 * One constant because the exact words matter (open-items review, item 8).
 * The mockups promised "trusted, licensed local pros", which asserts two
 * things the site does not do: maintain a vetted network, and run a formal
 * licensing check. What is true is narrower — an introduction to a
 * professional who works with a licensed HVAC contractor — so that is what
 * gets printed, everywhere, from here.
 */
export const PRO_NOUN = 'an HVAC professional working with a licensed HVAC contractor'
export const PRO_NOUN_SHORT = 'an HVAC professional'
export const CONNECT_CTA = 'Connect With an HVAC Professional'

export interface NavItem {
    id: string
    label: string
    href: string
    /** Inline SVG, 20x20 viewBox, currentColor fills. */
    icon: string
}

const svg = (body: string) =>
    `<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">${body}</svg>`

export const NAV_ITEMS: NavItem[] = [
    {
        id: 'home',
        label: 'Home',
        href: '/',
        icon: svg('<path d="M10 2.6 2.5 9h2v8h4v-5h3v5h4V9h2z"/>'),
    },
    {
        id: 'ask',
        label: 'Ask HVAC',
        href: '/ask',
        icon: svg('<path d="M3 3.5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H8l-4 3v-3H3a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1z"/>'),
    },
    {
        id: 'repair-replace',
        label: 'Repair or Replace',
        href: '/repair-or-replace',
        icon: svg('<path d="M10 2v3M3 6h14M5.4 6 3 12h4.8zM14.6 6 12 12h4.9zM7 17h6v-1.5H7z"/><path d="M9.2 5.4h1.6V17H9.2z"/>'),
    },
    {
        id: 'costs',
        label: 'Cost Guides',
        href: '/cost-guides',
        icon: svg('<path d="M10.9 2v1.4c1.7.2 2.9 1.1 3.1 2.7h-2.2c-.1-.6-.6-1-1.5-1-1 0-1.6.4-1.6 1s.4.9 1.9 1.2c2.4.5 3.5 1.3 3.5 3.1 0 1.7-1.2 2.8-3.2 3V15H9.2v-1.5c-2-.2-3.3-1.3-3.4-3h2.2c.1.8.8 1.3 1.9 1.3 1.1 0 1.7-.4 1.7-1.1 0-.6-.4-.9-2-1.3-2.3-.5-3.3-1.3-3.3-3C6.3 4.8 7.4 3.7 9.2 3.5V2z"/>'),
    },
    {
        id: 'systems',
        label: 'System Types',
        href: '/system-types',
        icon: svg('<path d="M10 6.6A3.4 3.4 0 1 0 10 13.4 3.4 3.4 0 0 0 10 6.6zm0 5a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2z"/><path d="M17 11.2v-2.4l-1.9-.3a5.4 5.4 0 0 0-.6-1.4l1.1-1.6-1.7-1.7-1.6 1.1a5.4 5.4 0 0 0-1.4-.6L10.6 2.4H8.2l-.3 1.9c-.5.1-1 .3-1.4.6L4.9 3.8 3.2 5.5l1.1 1.6c-.3.4-.5.9-.6 1.4l-1.9.3v2.4l1.9.3c.1.5.3 1 .6 1.4l-1.1 1.6 1.7 1.7 1.6-1.1c.4.3.9.5 1.4.6l.3 1.9h2.4l.3-1.9c.5-.1 1-.3 1.4-.6l1.6 1.1 1.7-1.7-1.1-1.6c.3-.4.5-.9.6-1.4z"/>'),
    },
    {
        id: 'iaq',
        label: 'IAQ',
        href: '/indoor-air-quality',
        icon: svg('<path d="M10 2.5c-1.6 3-5 4.6-5 8.1a5 5 0 0 0 10 0c0-3.5-3.4-5.1-5-8.1z"/>'),
    },
    {
        id: 'financing',
        label: 'Financing',
        href: '/financing',
        icon: svg('<path d="M3 4.5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1zm0 3h14v2H3z"/>'),
    },
    {
        id: 'about',
        label: 'About',
        href: '/about',
        icon: svg('<path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm.9 12.2H9.1V9h1.8zm0-6.6H9.1V5.8h1.8z"/>'),
    },
]

/** Where every "talk to someone" affordance goes. */
export const SECOND_OPINION_HREF = '/second-opinion'

/** Footer legal row. Rendered in this order; each has a page. */
export const LEGAL_LINKS = [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Use', href: '/terms' },
    { label: 'Disclaimer', href: '/disclaimer' },
]

/**
 * Whether the legal pages are final or still in review.
 *
 * 'review' prints a banner on all three saying so. Independent drafts are
 * being commissioned to compare against these (open-items review, item 11),
 * and a privacy policy that is quietly a draft is worse than one that says
 * it is. Flip to 'final' with the effective date once the comparison is
 * done and the wording is signed off.
 */
export const LEGAL_STATUS: 'review' | 'final' = 'review'
export const LEGAL_EFFECTIVE = 'August 15, 2026'
